import {memo, useCallback, useEffect, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {AppButton, AppButtonVariant} from '@/app/shared-components/AppButton';
import {COLORS} from '@/styles/colors';

import {SplitStatusPill} from './SplitStatusPill';

interface SplitResultActionsProps {
  onUndo: () => void;
  onAddSplit: () => void;
  isCreatingSplit?: boolean;
  splitCreateError?: boolean;
  isSplitAdded?: boolean;
}

export const SplitResultActions = memo(
  ({
    onUndo,
    onAddSplit,
    isCreatingSplit,
    splitCreateError,
    isSplitAdded,
  }: SplitResultActionsProps) => {
    const {t} = useTranslation();

    const [isCanceled, setIsCanceled] = useState(false);
    const [localIsSplitAdded, setLocalIsSplitAdded] = useState(false);

    const isError = splitCreateError && !localIsSplitAdded && !isCanceled;
    const isLoading =
      isCreatingSplit && !localIsSplitAdded && !isError && !isCanceled;

    const handleAddSplitClick = useCallback(() => {
      if (localIsSplitAdded || isLoading) return;
      onAddSplit();
    }, [localIsSplitAdded, isLoading, onAddSplit]);

    const handleUndoClick = useCallback(() => {
      onUndo();
      setIsCanceled(true);
    }, [onUndo]);

    const addSplitLabel = useMemo(
      () =>
        isError
          ? t('split_bills_page.retry_add_split')
          : t('split_bills_page.add_split'),
      [isError, t],
    );

    // Sync the prop into local state (once it becomes true, keep it true)
    useEffect(() => {
      if (isSplitAdded) setLocalIsSplitAdded(true);
    }, [isSplitAdded]);

    if (isError || isCanceled) {
      const isApiError = isError && !isCanceled;

      return (
        <div className="split-result-pill-column">
          <div className="split-result-actions">
            <SplitStatusPill
              id={isApiError ? 'split-result-error' : 'split-result-canceled'}
              text={t('split_bills_page.split_canceled')}
              fullWidth
              backgroundColor={COLORS.BACKGROUND_ERROR_PASTEL}
              textColor={COLORS.LIGHT_ERROR}
              icon={
                <img
                  src="/assets/icons/error-icon.svg"
                  alt={t('split_bills_page.split_canceled')}
                />
              }
            />
          </div>
        </div>
      );
    }

    if (localIsSplitAdded) {
      return (
        <div className="split-result-actions">
          <SplitStatusPill
            id="split-result-added-to-expenses"
            text={t('split_bills_page.added_to_expenses')}
            fullWidth
            backgroundColor={COLORS.ON_SURFACE_LIGHT_2}
            textColor={COLORS.ON_SURFACE_1}
            icon={
              <img
                src="/assets/icons/tick_circle.svg"
                alt={t('split_bills_page.added_to_expenses')}
              />
            }
          />
        </div>
      );
    }

    return (
      <div className="split-result-actions">
        <AppButton
          id="split-result-undo"
          label={t('split_bills_page.undo')}
          variant={AppButtonVariant.onQuinary}
          onClick={handleUndoClick}
          disabled={isLoading}
        />
        <AppButton
          id="split-result-add-split"
          label={addSplitLabel}
          variant={AppButtonVariant.onPrimary}
          onClick={handleAddSplitClick}
          isLoading={isLoading}
          disabled={isLoading}
        />
      </div>
    );
  },
);
